import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingWrapper, WhiteContainer } from "@components";
import { Box, Checkbox, Divider } from "@mui/material";

import AirConditioningIcon from "@assets/svg/AirConditioningIcon.svg?react";
import TvIcon from "@assets/svg/TvIcon.svg?react";
import SuppliesIcon from "@assets/svg/SuppliesIcon.svg?react";
import KitchenIcon from "@assets/svg/KitchenIcon.svg?react";
import DryerIcon from "@assets/svg/DryerIcon.svg?react";
import ClockIcon from "@assets/svg/ClockIcon.svg?react";

import ImageGalleryModal from "./components/ImageGalleryModal";
import FeesSection from "./components/FeesSection";
import DiscountSection from "./components/DiscountSection";
import AvailabilitySettingsSection from "./components/AvailabilitySettingsSection";
import ImageGridSkeleton from "./components/ImageGridSkeleton";
import { useForm, FormProvider } from "react-hook-form";

import {
  HeaderContainerGeneralSettings,
  WhiteContainerWrapper,
  HeaderTitleGeneralSettings,
  HeaderDescription,
  TabsContainer,
  TabButton,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  EditButton,
  FormContainer,
  FormLabel,
  FormInput,
  FormDivider,
  TimeIcon,
  TimeInput,
  FacilitiesContainer,
  FacilityItem,
  FacilityIcon,
  FacilityText,
  CancelButton,
  TimeInputContainerCheckIn,
  TimeInputContainerCheckOut,
  ServiceIconStyled,
  ButtonContainerStyled,
  PropertyImageStyled,
  ErrorMessageStyled,
  AmenitiesSectionStyled,
  AmenitiesContainerStyled,
  AmenityItemStyled,
  AmenityButtonStyled,
  AmenitiesDescriptionStyled,
  AmenitiesListContainerStyled,
  AmenityRowStyled,
  AmenityNameStyled,
  AmenitiesErrorStyled,
  FormTextArea,
} from "./styles";
import { usePropertySetting } from "../../services/usePropertySetting";
import { useToast } from "@context/ToastContext";

import {
  IPropertyBasicInfo,
  IPropertyFees,
  IPropertyDiscounts,
  IPropertyAmenities,
  IPropertyAvailabilitySettings,
} from "./types";

// Tab constants following TypeScript best practices
const TAB_KEYS = {
  PROPERTY_DETAILS: 'property-details',
  PRICING: 'pricing',
  AVAILABILITY_SETTINGS: 'availability-settings'
} as const;

type TabKey = typeof TAB_KEYS[keyof typeof TAB_KEYS];

//

const GeneraSetting = () => {
  // Helper function to format time to HH:mm format
  const formatTime = (time: string | undefined): string => {
    if (!time) return "";

    // If time is already in HH:mm format, return as is
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }

    // If time is in other formats, try to parse and format
    try {
      // Handle various time formats
      let hours = 0;
      let minutes = 0;

      if (time.includes(":")) {
        const parts = time.split(":");
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      } else if (time.includes(".")) {
        const parts = time.split(".");
        hours = parseInt(parts[0], 10);
        minutes = parseInt(parts[1], 10);
      } else {
        // Assume it's a number representing minutes since midnight
        const totalMinutes = parseInt(time, 10);
        hours = Math.floor(totalMinutes / 60);
        minutes = totalMinutes % 60;
      }

      // Ensure hours and minutes are within valid ranges
      hours = Math.max(0, Math.min(23, hours));
      minutes = Math.max(0, Math.min(59, minutes));

      // Format to HH:mm
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    } catch (error) {
      return time; // Return original if formatting fails
    }
  };

  // hooks
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // state
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.PROPERTY_DETAILS);
  const [open, setOpen] = useState(false);
  const [editAmenities, setEditAmenities] = useState(false);
  const [editPropertyInformation, setEditPropertyInformation] = useState(false);
  const [selectedAmenitiesParent, setSelectedAmenitiesParent] = useState("");
  const [selectedAmenitiesChild, setSelectedAmenitiesChild] = useState([]);

  // API hooks
  const {
    GetAmenitiesQuery,
    GetPropertyManagementQuery,
    GetPropertyImagesQuery,
    GetAvailabilityRuleSettingQuery,
    UpdatePropertyBasicInfoMutation,
    UpdatePropertyFeesMutation,
    UpdatePropertyDiscountsMutation,
    UpdatePropertyAmenitiesMutation,
    UpdatePropertyAvailabilitySettingsMutation,
  } = usePropertySetting();

  // queries
  const { data: amenities, isLoading: amenitiesLoading } = GetAmenitiesQuery();
  const {
    data: propertyManagement,
    isLoading: propertyManagementLoading,
    refetch: refetchPropertyManagement,
    isRefetching: isRefetchingPropertyManagement,
  } = GetPropertyManagementQuery(id || "");

  // Availability settings query - only enabled when the availability tab is active
  const {
    data: availabilityRuleSettings,
    isLoading: isLoadingAvailabilitySettings,
    refetch: refetchAvailabilitySettings,
  } = GetAvailabilityRuleSettingQuery(id || "", activeTab === TAB_KEYS.AVAILABILITY_SETTINGS);
  const { data: propertyImages, isLoading: propertyImagesLoading } =
    GetPropertyImagesQuery(id || "");

  const { mutate: updatePropertyBasicInfo, isPending: isUpdatingBasicInfo } =
    UpdatePropertyBasicInfoMutation();

  const { mutate: updatePropertyFees, isPending: isUpdatingFees } =
    UpdatePropertyFeesMutation();
  const { mutate: updatePropertyDiscounts, isPending: isUpdatingDiscounts } =
    UpdatePropertyDiscountsMutation();
  const { mutate: updatePropertyAmenities, isPending: isUpdatingAmenities } =
    UpdatePropertyAmenitiesMutation();
  
  // Availability settings mutation
  const { mutate: updatePropertyAvailabilitySettings, isPending: isUpdatingAvailabilitySettings } =
    UpdatePropertyAvailabilitySettingsMutation();

  // memo
  const propertyInformationData = useMemo(() => {
    const result = propertyManagement?.data?.result;
    setSelectedAmenitiesChild(result?.amenities?.map((item: any) => item?.key));

    return {
      id: result?.id,
      name: result?.name,
      description: result?.description,
      guestCount: result?.gusetCount,
      wifiName: result?.wifiName,
      wifiPassword: result?.wifiPassWord,
      checkInTime: result?.checkInTime,
      checkOutTime: result?.checkOutTime,
      roomCount: result?.roomCount,
      bedCount: result?.bedCount,
      bathroomCount: result?.bathroomCount,
      cleaningFee: result?.cleaningFee,
      managementFee: result?.managmentFee,
      communityFee: result?.communityFee,
      resortFee: result?.resortFee,
      laundryFees: result?.laundryFees,
      propertiesId: result?.propertiesId,
      images: result?.images,
      amenities: result?.amenities,
      channels: result?.channels,
      weeklyDiscount: result?.weeklyDiscount,
      monthlyDiscount: result?.monthlyDiscount,
      airbnbCommission: result?.airbnbCommission,
      gathernCommission: result?.gathernCommission,
      bookingCommission: result?.bookingCommission,
      msoolCommission: result?.msoolCommission,
      // Availability settings fields
      max_days_notice: result?.max_days_notice,
      booking_lead_time: result?.booking_lead_time,
      turnover_days: result?.turnover_days,
      default_max_nights: result?.default_max_nights,
      default_min_nights: result?.default_min_nights,
      day_of_week_check_in: result?.day_of_week_check_in,
      day_of_week_check_out: result?.day_of_week_check_out,
      day_of_week_min_nights: result?.day_of_week_min_nights,
    };
  }, [propertyManagement]);

  // Update form values when propertyInformationData changes
  useEffect(() => {
    setInitialFormValues();
  }, [propertyInformationData]);

  // Update availability settings form when API data is loaded
  useEffect(() => {
    setAvailabilityFormValues();
  }, [availabilityRuleSettings, propertyInformationData]);

  // React Hook Form setup for basic info
  const {
    register: registerBasicInfo,
    control: controlBasicInfo,
    handleSubmit: handleSubmitBasicInfo,
    setValue: setValueBasicInfo,
    watch: watchBasicInfo,
    reset: resetBasicInfo,
    trigger: triggerBasicInfo,
    formState: { errors: errorsBasicInfo },
  } = useForm<IPropertyBasicInfo>({
    defaultValues: {
      id: 0,
      name: "",
      description: "",
      wifiName: "",
      wifiPassWord: "",
      roomCount: 0,
      bedCount: 0,
      bathroomCount: 0,
      checkInTime: "",
      checkOutTime: "",
    },
  });

  // Debug: Log current form values
  useEffect(() => {
    const subscription = watchBasicInfo((value, { name, type }) => {});
    return () => subscription.unsubscribe();
  }, [watchBasicInfo]);


  // React Hook Form setup for fees
  const {
    register: registerFees,
    control: controlFees,
    handleSubmit: handleSubmitFees,
    setValue: setValueFees,
    watch: watchFees,
    reset: resetFees,
    trigger: triggerFees,
    formState: { errors: errorsFees },
  } = useForm<IPropertyFees>({
    defaultValues: {
      id: 0,
      cleaningFee: 0,
      managmentFee: 0,
      communityFee: 0,
      resortFee: 0,
      laundryFees: 0,
    },
  });

  // React Hook Form setup for discounts
  const {
    register: registerDiscounts,
    control: controlDiscounts,
    handleSubmit: handleSubmitDiscounts,
    setValue: setValueDiscounts,
    watch: watchDiscounts,
    reset: resetDiscounts,
    trigger: triggerDiscounts,
    formState: { errors: errorsDiscounts },
  } = useForm<IPropertyDiscounts>({
    defaultValues: {
      id: 0,
      monthlyDiscount: 0,
      weeklyDiscount: 0,
    },
  });

  // React Hook Form setup for amenities
  const {
    register: registerAmenities,
    control: controlAmenities,
    handleSubmit: handleSubmitAmenities,
    setValue: setValueAmenities,
    watch: watchAmenities,
    reset: resetAmenities,
    trigger: triggerAmenities,
    formState: { errors: errorsAmenities },
  } = useForm<IPropertyAmenities>({
    defaultValues: {
      id: 0,
      amenities: [],
    },
  });

  // React Hook Form setup for availability settings
  const availabilitySettingsMethods = useForm<IPropertyAvailabilitySettings>({
    defaultValues: {
      id: 0,
      max_days_notice: -1, // Default to "All Dates Available"
      booking_lead_time: 1, // Default to 1 hour
      turnover_days: 0, // Default to 0 days
      default_max_nights: 1, // Default to 1 night
      default_min_nights: 1, // Default to 1 night
      day_of_week_check_in: Array(7).fill(false), // Default all days to false
      day_of_week_check_out: Array(7).fill(false), // Default all days to false
      day_of_week_min_nights: Array(7).fill(0), // Default all days to 0
    },
  });

  const {
    register: registerAvailabilitySettings,
    control: controlAvailabilitySettings,
    handleSubmit: handleSubmitAvailabilitySettings,
    setValue: setValueAvailabilitySettings,
    watch: watchAvailabilitySettings,
    reset: resetAvailabilitySettings,
    trigger: triggerAvailabilitySettings,
    formState: { errors: errorsAvailabilitySettings },
  } = availabilitySettingsMethods;

  const setInitialFormValues = () => {
    if (propertyInformationData) {
      resetBasicInfo({
        id: propertyInformationData.id,
        name: propertyInformationData.name,
        description: propertyInformationData.description,
        wifiName: propertyInformationData.wifiName,
        wifiPassWord: propertyInformationData.wifiPassword,
        roomCount: propertyInformationData.roomCount,
        bedCount: propertyInformationData.bedCount,
        bathroomCount: propertyInformationData.bathroomCount,
        checkInTime: formatTime(propertyInformationData.checkInTime),
        checkOutTime: formatTime(propertyInformationData.checkOutTime),
      });

      // Set fees form values
      resetFees({
        id: propertyInformationData.id,
        cleaningFee: propertyInformationData?.cleaningFee || 0,
        managmentFee: propertyInformationData?.managementFee || 0,
        communityFee: propertyInformationData?.communityFee || 0,
        resortFee: propertyInformationData?.resortFee || 0,
        laundryFees: propertyInformationData?.laundryFees || 0,
      });

      // Set amenities form values
      resetAmenities({
        id: propertyInformationData.id,
        amenities: propertyInformationData?.amenities || [],
      });
    }
  };

  // Separate function to set availability settings form values
  const setAvailabilityFormValues = () => {
    if (availabilityRuleSettings?.data?.result && propertyInformationData?.id) {
      const result = availabilityRuleSettings.data.result;
      resetAvailabilitySettings({
        id: propertyInformationData.id,
        max_days_notice: result.max_days_notice ?? -1,
        booking_lead_time: result.booking_lead_time ?? 1,
        turnover_days: result.turnover_days ?? 0,
        default_max_nights: result.default_max_nights ?? 1,
        default_min_nights: result.default_min_nights ?? 1,
        day_of_week_check_in: result.day_of_week_check_in ?? Array(7).fill(false),
        day_of_week_check_out: result.day_of_week_check_out ?? Array(7).fill(false),
        day_of_week_min_nights: result.day_of_week_min_nights ?? Array(7).fill(0),
      });
    }
  };

  const onSubmitBasicInfo = handleSubmitBasicInfo(async (data) => {
    // ...
    updatePropertyBasicInfo(data, {
      onSuccess: () => {
        setEditPropertyInformation(false);
        setEditAmenities(false);
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitFees = handleSubmitFees(async (data) => {
    updatePropertyFees(data, {
      onSuccess: () => {
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitDiscounts = handleSubmitDiscounts(async (data) => {
    updatePropertyDiscounts(data, {
      onSuccess: () => {
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitAmenities = handleSubmitAmenities(async (data) => {
    updatePropertyAmenities(data, {
      onSuccess: () => {
        setEditAmenities(false);
        refetchPropertyManagement();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const onSubmitAvailabilitySettings = handleSubmitAvailabilitySettings(async (data) => {
    updatePropertyAvailabilitySettings({ propertyId: id || "", data }, {
      onSuccess: () => {
        refetchPropertyManagement();
        refetchAvailabilitySettings();
        showToast(t("PROPERTIES_MANAGEMENT.PROPERTY_UPDATED"), {
          severity: "success",
        });
      },
    });
  });

  const tabs = [
    {
      label: t("PROPERTIES_MANAGEMENT.PROPERTY_DETAILS"),
      value: TAB_KEYS.PROPERTY_DETAILS,
    },
    {
      label: t("PROPERTIES_MANAGEMENT.PRICING"),
      value: TAB_KEYS.PRICING,
    },
    {
      label: t("PROPERTIES_MANAGEMENT.AVAILABILITY_SETTINGS"),
      value: TAB_KEYS.AVAILABILITY_SETTINGS,
    },
  ];
  const servicesIcons = [
    <ServiceIconStyled>
      <AirConditioningIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <TvIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <SuppliesIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <DryerIcon />
    </ServiceIconStyled>,
    <ServiceIconStyled>
      <KitchenIcon />
    </ServiceIconStyled>,
  ];

  return (
    <Box>
      {/* Header  */}
      <HeaderContainerGeneralSettings>
        <HeaderTitleGeneralSettings
          onClick={() => navigate("/channels/properties")}
        >
          {t("PROPERTIES_MANAGEMENT.ALL_PROPERTIES")}
        </HeaderTitleGeneralSettings>
        <HeaderTitleGeneralSettings>/</HeaderTitleGeneralSettings>
        <HeaderTitleGeneralSettings>
          {propertyInformationData?.name}
        </HeaderTitleGeneralSettings>
      </HeaderContainerGeneralSettings>
      {/* Container */}
      <WhiteContainerWrapper>
        <WhiteContainer borderRadius="0" border="0" >
          {/* tabs part  */}
          <TabsContainer>
            {tabs.map((tab) => (
              <TabButton
                key={tab.value}
                active={activeTab === tab.value}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabsContainer>
          {activeTab === TAB_KEYS.PROPERTY_DETAILS && (
            <>
              {/* Image Content Section  */}
              <SectionHeader>
                <SectionTitle>{t("PROPERTIES_MANAGEMENT.IMAGES")}</SectionTitle>
                <EditButton onClick={() => setOpen(true)}>
                  {t("PROPERTIES_MANAGEMENT.EDIT")}
                </EditButton>
              </SectionHeader>
              <LoadingWrapper loading={propertyManagementLoading}>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-[7px] p-6">
                  {propertyManagementLoading ? (
                    <ImageGridSkeleton count={7} />
                  ) : (
                    propertyImages?.data?.result
                      ?.filter((item: any) => item?.image)
                      ?.map((item: any) => (
                        <div
                          key={item?.id}
                          className="aspect-[4/3] relative overflow-hidden rounded-lg"
                        >
                          <PropertyImageStyled
                            src={item?.image}
                            alt="property"
                          />
                        </div>
                      ))
                  )}
                </div>
              </LoadingWrapper>
              <FormDivider />
              {/* Property Information Section  */}
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.PROPERTY_INFORMATION")}
                </SectionTitle>
                {!editPropertyInformation && (
                  <EditButton onClick={() => setEditPropertyInformation(true)}>
                    {t("PROPERTIES_MANAGEMENT.EDIT")}
                  </EditButton>
                )}
                {editPropertyInformation && (
                  <ButtonContainerStyled>
                    <EditButton
                      onClick={() => {
                        setEditPropertyInformation(false);
                        setInitialFormValues();
                      }}
                    >
                      {t("COMMON.CANCEL")}
                    </EditButton>
                    <CancelButton
                      onClick={() => {
                        onSubmitBasicInfo();
                      }}
                    >
                      {t("COMMON.SAVE")}
                    </CancelButton>
                  </ButtonContainerStyled>
                )}
              </SectionHeader>
              {/* form section  */}
              <LoadingWrapper loading={isUpdatingBasicInfo}>
                <FormContainer>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.PROPERTY_NAME")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("name", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("name") || ""}
                  />
                  {errorsBasicInfo.name && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.name.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.DESCRIPTION")}
                  </FormLabel>
                  <FormTextArea
                    disabled={!editPropertyInformation}
                    {...registerBasicInfo("description", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("description") || ""}
                  />
                  {errorsBasicInfo.description && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.description.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.INTERNET_CONNECTION")}
                  </SectionSubtitle>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.WIFI_NETWORK_NAME")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("wifiName", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("wifiName") || ""}
                  />
                  {errorsBasicInfo.wifiName && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.wifiName.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.WIFI_PASSWORD")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="text"
                    {...registerBasicInfo("wifiPassWord", {
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                    })}
                    value={watchBasicInfo("wifiPassWord") || ""}
                  />
                  {errorsBasicInfo.wifiPassWord && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.wifiPassWord.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.ROOMS")}
                  </SectionSubtitle>
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_ROOMS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("roomCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t(
                          "PROPERTIES_MANAGEMENT.ROOM_COUNT_MIN_VALUE"
                        ),
                      },
                    })}
                    value={watchBasicInfo("roomCount") || 0}
                  />
                  {errorsBasicInfo.roomCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.roomCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_BEDS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("bedCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t("PROPERTIES_MANAGEMENT.BED_COUNT_MIN_VALUE"),
                      },
                    })}
                    value={watchBasicInfo("bedCount") || 0}
                  />
                  {errorsBasicInfo.bedCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.bedCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormLabel>
                    {t("PROPERTIES_MANAGEMENT.NUMBER_OF_BATHROOMS")}
                  </FormLabel>
                  <FormInput
                    disabled={!editPropertyInformation}
                    type="number"
                    {...registerBasicInfo("bathroomCount", {
                      valueAsNumber: true,
                      required: t(
                        "PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"
                      ),
                      min: {
                        value: 1,
                        message: t(
                          "PROPERTIES_MANAGEMENT.BATHROOM_COUNT_MIN_VALUE"
                        ),
                      },
                    })}
                    value={watchBasicInfo("bathroomCount") || 0}
                  />
                  {errorsBasicInfo.bathroomCount && (
                    <ErrorMessageStyled>
                      {errorsBasicInfo.bathroomCount.message}
                    </ErrorMessageStyled>
                  )}
                  <FormDivider />
                  <SectionSubtitle>
                    {t("PROPERTIES_MANAGEMENT.BOOKING_POLICY")}
                  </SectionSubtitle>
                  <FormLabel>{t("PROPERTIES_MANAGEMENT.CHECK_IN")}</FormLabel>
                  <TimeInputContainerCheckIn>
                    {!editPropertyInformation && (
                      <TimeIcon>
                        <ClockIcon />
                      </TimeIcon>
                    )}
                    <TimeInput
                      {...registerBasicInfo("checkInTime")}
                      type="time"
                      disabled={!editPropertyInformation}
                      value={formatTime(watchBasicInfo("checkInTime"))}
                      onChange={(e) => {
                        setValueBasicInfo("checkInTime", e.target.value);
                      }}
                    />
                  </TimeInputContainerCheckIn>
                  <FormLabel>{t("PROPERTIES_MANAGEMENT.CHECK_OUT")}</FormLabel>
                  <TimeInputContainerCheckOut>
                    {!editPropertyInformation && (
                      <TimeIcon>
                        <ClockIcon />
                      </TimeIcon>
                    )}
                    <TimeInput
                      {...registerBasicInfo("checkOutTime")}
                      type="time"
                      disabled={!editPropertyInformation}
                      value={formatTime(watchBasicInfo("checkOutTime"))}
                      onChange={(e) => {
                        setValueBasicInfo("checkOutTime", e.target.value);
                      }}
                    />
                  </TimeInputContainerCheckOut>
                  <FormDivider />
                </FormContainer>
              </LoadingWrapper>
              {/* Facilities and supplies section  */}
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.FACILITIES_AND_SUPPLIES")}
                </SectionTitle>
                {!editAmenities && (
                  <EditButton onClick={() => setEditAmenities(true)}>
                    {t("PROPERTIES_MANAGEMENT.EDIT")}
                  </EditButton>
                )}
                {editAmenities && (
                  <ButtonContainerStyled>
                    <EditButton
                      onClick={() => {
                        setInitialFormValues();
                        setEditAmenities(false);
                      }}
                    >
                      {t("COMMON.CANCEL")}
                    </EditButton>
                    <CancelButton
                      onClick={() => {
                        // Check if amenities are selected
                        if (
                          selectedAmenitiesChild.length ===
                          watchAmenities("amenities").length
                        ) {
                          return;
                        }
                        onSubmitAmenities();
                      }}
                    >
                      {t("COMMON.SAVE")}
                    </CancelButton>
                  </ButtonContainerStyled>
                )}
              </SectionHeader>
              {/* List of facilities and supplies  */}
              <LoadingWrapper loading={isUpdatingAmenities}>
                {editAmenities && (
                  <AmenitiesSectionStyled>
                    <AmenitiesContainerStyled>
                      {amenities?.data?.result?.map((item: any) => (
                        <AmenityItemStyled
                          onClick={() => {
                            setSelectedAmenitiesParent(item?.name);
                          }}
                          key={item?.id}
                        >
                          <AmenityButtonStyled
                            isSelected={selectedAmenitiesParent === item?.name}
                          >
                            {item?.name}
                          </AmenityButtonStyled>
                        </AmenityItemStyled>
                      ))}
                    </AmenitiesContainerStyled>
                    <AmenitiesDescriptionStyled>
                      {t("PROPERTIES_MANAGEMENT.ADD_AMENITIES_DESCRIPTION")}
                    </AmenitiesDescriptionStyled>
                    {selectedAmenitiesParent && (
                      <AmenitiesListContainerStyled>
                        {amenities?.data?.result
                          ?.find(
                            (item: any) =>
                              item?.name === selectedAmenitiesParent
                          )
                          ?.amenities?.map((item: any) => (
                            <AmenityRowStyled
                              key={item?.id}
                              isSelected={propertyInformationData?.amenities?.some(
                                (amenity: any) => amenity.key === item?.key
                              )}
                            >
                              <Checkbox
                                checked={watchAmenities("amenities")?.some(
                                  (amenity: any) => amenity.key === item?.key
                                )}
                                onChange={(e) => {
                                  const currentAmenities =
                                    watchAmenities("amenities") || [];
                                  if (e.target.checked) {
                                    setValueAmenities("amenities", [
                                      ...currentAmenities,
                                      {
                                        name: item.name,
                                        key: item.key,
                                        isActive: true,
                                      },
                                    ]);
                                  } else {
                                    setValueAmenities(
                                      "amenities",
                                      currentAmenities.filter(
                                        (amenity: any) =>
                                          amenity.key !== item.key
                                      )
                                    );
                                  }
                                }}
                              />
                              <ServiceIconStyled>
                                <DryerIcon />
                              </ServiceIconStyled>
                              <AmenityNameStyled>
                                {item?.name}
                              </AmenityNameStyled>
                            </AmenityRowStyled>
                          ))}
                      </AmenitiesListContainerStyled>
                    )}

                    {/* Error message for no amenities selected */}
                    {selectedAmenitiesChild.length ===
                      watchAmenities("amenities").length && (
                      <AmenitiesErrorStyled isRTL={isRTL}>
                        {t("PROPERTIES_MANAGEMENT.AMENITIES_REQUIRED")}
                      </AmenitiesErrorStyled>
                    )}
                  </AmenitiesSectionStyled>
                )}
              </LoadingWrapper>

              {!editAmenities && (
                <FacilitiesContainer>
                  {propertyInformationData?.amenities?.map((service: any) => (
                    <FacilityItem key={service.key}>
                      <FacilityIcon>
                        <DryerIcon />
                      </FacilityIcon>
                      <FacilityText>{service.name}</FacilityText>
                    </FacilityItem>
                  ))}
                </FacilitiesContainer>
              )}

              {/* House Rules section */}
              <FormDivider />
              <SectionHeader>
                <SectionTitle>
                  {t("PROPERTIES_MANAGEMENT.HOUSE_RULES")}
                </SectionTitle>
              </SectionHeader>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionSubtitle sx={{ fontWeight: 400, fontSize: '13px', color: '#475569', lineHeight: 1.7 }}>
                  {t("PROPERTIES_MANAGEMENT.HOUSE_RULES_DESCRIPTION")}
                </SectionSubtitle>
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate('/messaging')}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6563FF',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: 0,
                    textAlign: isRTL ? 'right' : 'left',
                    alignSelf: 'flex-start',
                    fontFamily: 'inherit',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  ← {t("PROPERTIES_MANAGEMENT.GO_TO_ACCESS_GUIDE")}
                </Box>
              </Box>
            </>
          )}
          {activeTab === TAB_KEYS.PRICING && (
            <>
              {/* form section  */}

              <LoadingWrapper loading={isUpdatingFees}>
                <FormContainer>
                  <FeesSection
                    reset={setInitialFormValues}
                    fees={{
                      cleaningFee: propertyInformationData?.cleaningFee,
                      managementFee: propertyInformationData?.managementFee,
                      communityFee: propertyInformationData?.communityFee,
                      resortFee: propertyInformationData?.resortFee,
                      laundryFees: propertyInformationData?.laundryFees,
                    }}
                    register={registerFees}
                    control={controlFees}
                    trigger={triggerFees}
                    onSave={onSubmitFees}
                  />
                </FormContainer>
              </LoadingWrapper>

              <FormDivider />

              <LoadingWrapper loading={isUpdatingDiscounts}>
                <FormContainer>
                  <DiscountSection
                    control={controlDiscounts}
                    reset={setInitialFormValues}
                    register={registerDiscounts}
                    trigger={triggerDiscounts}
                    onSave={onSubmitDiscounts}
                    discounts={{
                      weekly: propertyInformationData?.weeklyDiscount,
                      monthly: propertyInformationData?.monthlyDiscount,
                    }}
                  />
                </FormContainer>
              </LoadingWrapper>
            </>
          )}
          {activeTab === TAB_KEYS.AVAILABILITY_SETTINGS && (
            <>
              {/* availability settings tab  */}
              <LoadingWrapper loading={isUpdatingAvailabilitySettings || isLoadingAvailabilitySettings}>
                <FormContainer>
                  <FormProvider {...availabilitySettingsMethods}>
                    <AvailabilitySettingsSection
                      availabilitySettings={{
                        max_days_notice: availabilityRuleSettings?.data?.result?.max_days_notice,
                        booking_lead_time: availabilityRuleSettings?.data?.result?.booking_lead_time,
                        turnover_days: availabilityRuleSettings?.data?.result?.turnover_days,
                        default_max_nights: availabilityRuleSettings?.data?.result?.default_max_nights,
                        default_min_nights: availabilityRuleSettings?.data?.result?.default_min_nights,
                        day_of_week_check_in: availabilityRuleSettings?.data?.result?.day_of_week_check_in,
                        day_of_week_check_out: availabilityRuleSettings?.data?.result?.day_of_week_check_out,
                        day_of_week_min_nights: availabilityRuleSettings?.data?.result?.day_of_week_min_nights,
                      }}
                      register={registerAvailabilitySettings}
                      control={controlAvailabilitySettings}
                      onSave={onSubmitAvailabilitySettings}
                      reset={setInitialFormValues}
                      trigger={triggerAvailabilitySettings}
                    />
                  </FormProvider>
                </FormContainer>
              </LoadingWrapper>
            </>
          )}
        </WhiteContainer>
      </WhiteContainerWrapper>
      {/* Image Gallery Modal */}
      <ImageGalleryModal
        open={open}
        onClose={() => setOpen(false)}
        isRTL={isRTL}
        propertyId={id}
      />
      {/* Time Selection Modal */}
    </Box>
  );
};

export default GeneraSetting;
